import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logoutAdmin, setActiveTab } from '../../Redux/adminSlice';
import { 
  FiHome, 
  FiUsers, 
  FiBriefcase, 
  FiLink, 
  FiSettings, 
  FiLogOut, 
  FiMenu, 
  FiX,
  FiTrendingUp,
  FiShoppingCart,
  FiMonitor
} from 'react-icons/fi';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { admin, activeTab } = useSelector((state) => state.admin);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Authentication is now handled in AdminRoutes.js
  
  // Handle tab selection based on URL path
  useEffect(() => {
    // Extract the route after /auth/admin/
    const pathParts = location.pathname.split('/');
    const adminIndex = pathParts.indexOf('admin');
    const route = adminIndex !== -1 && pathParts[adminIndex + 1] 
      ? pathParts[adminIndex + 1] 
      : 'dashboard';
    
    // Map route to tab ID
    const routeToTabMap = {
      'dashboard': 'dashboard',
      'users': 'users',
      'companies': 'companies',
      'affiliate': 'affiliate',
      'instagram-analytics': 'instagram-analytics',
      'settings': 'settings',
      'marketplace': 'marketplace',
      'advertising': 'advertising'
    };
    
    const tabId = routeToTabMap[route] || 'dashboard';
    dispatch(setActiveTab(tabId));
  }, [location.pathname, dispatch]);
  
  const handleLogout = () => {
    dispatch(logoutAdmin());
    navigate('/auth/admin/login');
  };
  
  const navigationItems = [
    { 
      id: 'dashboard', 
      label: 'Dashboard', 
      icon: <FiHome className="w-5 h-5" />, 
      path: '/auth/admin/dashboard' 
    },
    { 
      id: 'users', 
      label: 'Influencers', 
      icon: <FiUsers className="w-5 h-5" />, 
      path: '/auth/admin/users' 
    },
    { 
      id: 'companies', 
      label: 'Companies', 
      icon: <FiBriefcase className="w-5 h-5" />, 
      path: '/auth/admin/companies' 
    },
    { 
      id: 'marketplace', 
      label: 'Marketplace', 
      icon: <FiShoppingCart className="w-5 h-5" />, 
      path: '/auth/admin/marketplace' 
    },
    { 
      id: 'affiliate', 
      label: 'Affiliate', 
      icon: <FiLink className="w-5 h-5" />, 
      path: '/auth/admin/affiliate' 
    },
    { 
      id: 'advertising', 
      label: 'Advertising', 
      icon: <FiMonitor className="w-5 h-5" />, 
      path: '/auth/admin/advertising' 
    },
    { 
      id: 'instagram-analytics', 
      label: 'Instagram Analytics', 
      icon: <FiTrendingUp className="w-5 h-5" />, 
      path: '/auth/admin/instagram-analytics' 
    },
    { 
      id: 'settings', 
      label: 'Settings', 
      icon: <FiSettings className="w-5 h-5" />, 
      path: '/auth/admin/settings' 
    },
  
  ];
  
  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-gray-800 text-white transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-700">
          <div className="text-xl font-bold text-orange-500">Infuber Admin</div>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-300 hover:text-white"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>
        
        <nav className="mt-5 px-4">
          <ul className="space-y-2">
            {navigationItems.map((item) => (
              <li key={item.id}>
                <a
                  href={item.path}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`flex items-center px-4 py-3 rounded-md transition-colors ${
                    activeTab === item.id 
                      ? 'bg-gray-900 text-white' 
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  {item.icon}
                  <span className="ml-3">{item.label}</span>
                </a>
              </li>
            ))}
            
            <li className="mt-6 pt-6 border-t border-gray-700">
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-3 text-gray-300 hover:bg-gray-700 hover:text-white rounded-md transition-colors"
              >
                <FiLogOut className="w-5 h-5" />
                <span className="ml-3">Logout</span>
              </button>
            </li>
          </ul>
        </nav>
      </aside>
      
      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm z-10">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <FiMenu className="w-6 h-6" />
            </button>
            
            <div className="flex items-center">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-700">
                  {admin?.username || 'Admin User'}
                </p>
                <p className="text-xs text-gray-500">
                  {admin?.role || 'Administrator'}
                </p>
              </div>
              <div className="ml-3 w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white">
                {admin?.username?.charAt(0).toUpperCase() || 'A'}
              </div>
            </div>
          </div>
        </header>
        
        {/* Main content */}
        <main className="flex-1 overflow-y-auto bg-gray-100">
          <div className="p-4 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout; 